import React, { useState, useEffect } from 'react';
import { CancelToken } from 'axios';
import { useSnackbar } from 'notistack';
import { CircularProgressCentered } from '../../../circular-progress-centered/circular-progress-centered';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useApi, useApiOnRender, useErrorSnackbar } from '../../../../hooks';
import { GetUserByIdResponse, GetUserByIdRequest, UpsertResponsibleCaseWorkerOrTeamRequest, UpsertResponsibleCaseWorkerOrTeamResponse } from '../../../../api-service/clients';
import { AssignCaseworkerDialogComponent } from './assign-caseworker-dialog-component';
import { useAuthenticatedUser } from '../../../../authentication';

export interface IAssignCaseworkerDialogProps {
  companyIds?: string[];
}

export function AssignCaseworkerCompanyDialog({ companyIds }: IAssignCaseworkerDialogProps) {
  const [userId, setUserId] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const organizationClient = moduleClientFactory.createOrganizationClient();
  const companyDetailsClient = moduleClientFactory.createCompanyDetailsClient();

  const authenticatedUser = useAuthenticatedUser();

  const currentUserApi = useApiOnRender(() => organizationClient.getUserById({ userId: authenticatedUser?.profile.id as string }), {} as GetUserByIdResponse);
  const upsertResponsibleCaseWorkerOrTeamApi = useApi(
    (cancelToken: CancelToken, request: UpsertResponsibleCaseWorkerOrTeamRequest) => companyDetailsClient.upsertResponsibleCaseWorkerOrTeam(request, cancelToken),
    {} as UpsertResponsibleCaseWorkerOrTeamResponse
  );
  const getUserByIdApi = useApi((cancelToken, request: GetUserByIdRequest) => organizationClient.getUserById(request, cancelToken), {} as GetUserByIdResponse);

  useErrorSnackbar(currentUserApi.error, upsertResponsibleCaseWorkerOrTeamApi.error);

  useEffect(() => {
    if (currentUserApi.isReady) {
      setUserId(currentUserApi.result.user?.id);
      setTeamId(currentUserApi.result.user?.teamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserApi.isReady]);

  const handleConfirm = async () => {
    const request: UpsertResponsibleCaseWorkerOrTeamRequest = {
      responsibleTeamId: teamId ?? '',
      companyIds,
      responsibleCaseWorkerId: userId,
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response = await upsertResponsibleCaseWorkerOrTeamApi.invoke(request);
    if (response.result.companyIds) {
      enqueueSnackbar('Ansvarlig sagsbehandler og team blev tildelt', { variant: 'success' });
    }
    setIsOpen(false);
  };

  const handleTeamChange = (value: string | undefined) => {
    setTeamId(value);
    setUserId(undefined);
  };

  const handleUserChange = async (selectedUserId: string | undefined) => {
    setUserId(selectedUserId);

    if (selectedUserId) {
      const response = await getUserByIdApi.invoke({ userId: selectedUserId });

      if (response.result) {
        const { user } = response.result;
        setTeamId(user?.teamId);
      }
    } else {
      setTeamId(currentUserApi.result.user?.teamId);
    }
  };
  function handleClose() {
    setIsOpen(false);
  }
  return currentUserApi.isReady ? (
    <AssignCaseworkerDialogComponent
      onConfirm={handleConfirm}
      onTeamChange={handleTeamChange}
      onUserChange={handleUserChange}
      teamId={teamId}
      userId={userId}
      isOpen={isOpen}
      onClose={handleClose}
    />
  ) : (
    <CircularProgressCentered />
  );
}
